import BrushOutlined from "@mui/icons-material/BrushOutlined";
import CardGiftcardOutlined from "@mui/icons-material/CardGiftcardOutlined";
import Hearing from "@mui/icons-material/Hearing";
import LocalMallOutlined from "@mui/icons-material/LocalMallOutlined";
import SmsOutlined from "@mui/icons-material/SmsOutlined";
import Star from "@mui/icons-material/Star";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import { Box, Stack } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { AdditionalServicesModal } from "../../../additional-services/additional-services-modal";
import type {
  AdditionalServicesType,
  PossibleUpsellServices,
} from "../../../additional-services/type";
import { useAccountLimitsContext } from "../../../context/account-limit-context";
import { useUpsellServices } from "../../../generic/hooks/use-upsell-services";
import { listenEnabled } from "../../../generic/server/supabase-account-service";
import { ThemeAwareDivider } from "./divider";
import { Item } from "./item";

export const AdditionalServicesSection: FC = () => {
  const { t } = useTranslation();
  const dialog = useDialog<{ service: AdditionalServicesType }>();

  const { listen_entitlement } = useAccountLimitsContext();
  const { loading, data: upsoldServices } = useUpsellServices();

  const serviceEnabled = (service: PossibleUpsellServices): boolean => {
    if (loading || !upsoldServices) return false;
    return upsoldServices[service] ?? false;
  };

  const allServiceItems: PossibleUpsellServices[] = [
    "enable_twilio",
    "enable_listen",
    "enable_swag",
    "enable_e_gifting",
    "enable_videography",
    "enable_design_services",
  ];

  const filteredItems = new Set(
    allServiceItems.filter((service) => {
      if (service === "enable_listen") {
        return !listenEnabled(listen_entitlement) && !serviceEnabled(service);
      }

      return !serviceEnabled(service);
    }),
  );

  if (loading || !upsoldServices || filteredItems.size === 0) {
    return <ThemeAwareDivider />;
  }

  return (
    <>
      <Stack
        component="ul"
        spacing={0.5}
        sx={{
          background: "#3741517A",
          listStyle: "none",
          m: 0,
          p: 2,
          borderRadius: 1,
          border: `1px solid #3741517A`,
        }}
      >
        <Box
          component="li"
          sx={{
            color: "var(--nav-section-title-color)",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.66,
            mb: 1,
            ml: 1,
            textTransform: "uppercase",
          }}
        >
          <Box data-testid="when" sx={{ display: "inline-block" }}>
            <Star sx={{ verticalAlign: "middle", mr: 1, color: "#FFD401" }} />
            {t("Additional Services")}
          </Box>
        </Box>
        {filteredItems.has("enable_twilio") && (
          <Item
            title={t("SMS")}
            icon={<SmsOutlined sx={{ color: "#FFD401" }} />}
            onClick={{ type: "function", value: () => dialog.handleOpen({ service: "sms" }) }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-sms"
          />
        )}
        {!listenEnabled(listen_entitlement) && filteredItems.has("enable_listen") && (
          <Item
            title={t("Listen")}
            icon={<Hearing sx={{ color: "#FFD401" }} />}
            onClick={{
              type: "function",
              value: () => dialog.handleOpen({ service: "listen" }),
            }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-listen"
          />
        )}
        {filteredItems.has("enable_swag") && (
          <Item
            title={t("Swag")}
            icon={<LocalMallOutlined sx={{ color: "#FFD401" }} />}
            onClick={{ type: "function", value: () => dialog.handleOpen({ service: "swag" }) }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-swag"
          />
        )}
        {filteredItems.has("enable_e_gifting") && (
          <Item
            title={t("E-Gifting")}
            icon={<CardGiftcardOutlined sx={{ color: "#FFD401" }} />}
            onClick={{ type: "function", value: () => dialog.handleOpen({ service: "egifting" }) }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-egifting"
          />
        )}
        {filteredItems.has("enable_videography") && (
          <Item
            title={t("Videography")}
            icon={<VideocamOutlined sx={{ color: "#FFD401" }} />}
            onClick={{
              type: "function",
              value: () => dialog.handleOpen({ service: "videography" }),
            }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-videography"
          />
        )}
        {filteredItems.has("enable_design_services") && (
          <Item
            title={t("Design Services")}
            icon={<BrushOutlined sx={{ color: "#FFD401" }} />}
            onClick={{
              type: "function",
              value: () => dialog.handleOpen({ service: "designservices" }),
            }}
            active={() => false}
            data-analytics-id="sidebar-additional-services-design-services"
          />
        )}
      </Stack>
      <AdditionalServicesModal dialog={dialog} />
    </>
  );
};
